import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const HomePage = () => {
  const [counts, setCounts] = useState({ cranes: 0, years: 0, projects: 0, clients: 0 });
  const statsRef = useRef<HTMLDivElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animateCounters();
        }
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateCounters = () => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const targets = { cranes: 300, years: 15, projects: 500, clients: 150 };

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;

      setCounts({
        cranes: Math.floor(targets.cranes * progress),
        years: Math.floor(targets.years * progress),
        projects: Math.floor(targets.projects * progress),
        clients: Math.floor(targets.clients * progress),
      });

      if (step >= steps) {
        clearInterval(timer);
        setCounts(targets);
      }
    }, interval);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/7b38cb50ec253bf65ef7b38099da6cd4.jpg"
            alt="رافعات ضخمة في موقع بناء"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/70 via-primary/50 to-primary/70"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-7xl md:text-8xl font-black text-white mb-6 leading-tight">
                قوة البناء...
                <br />
                <span className="text-accent">ثقة الإنجاز</span>
              </h1>
              <p className="text-2xl md:text-3xl text-accent font-bold mb-4">
                +15 عاماً من الخبرة | +300 رافعة | رؤية 2030
              </p>
              <p className="text-xl md:text-2xl text-white/90 mb-12 leading-relaxed max-w-3xl">
                شريكك الموثوق في تأجير الرافعات والمعدات الثقيلة لأضخم المشاريع الإنشائية في المملكة
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href="#contact"
                  className="bg-accent hover:bg-accent-dark text-primary px-8 py-4 rounded-lg font-black text-lg transition-all shadow-2xl hover:shadow-accent/50 hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center gap-3"
                >
                  <span>احصل على عرض سعر فوري</span>
                  <i className="ri-arrow-left-line text-xl"></i>
                </a>
                <Link
                  to="/services"
                  className="bg-transparent border-4 border-white hover:bg-white hover:text-primary text-white px-8 py-4 rounded-lg font-bold text-lg transition-all whitespace-nowrap cursor-pointer inline-flex items-center gap-3"
                >
                  <span>تصفح أسطول الرافعات</span>
                  <i className="ri-truck-line text-xl"></i>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="bg-primary py-20 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'ri-truck-line', value: counts.cranes, suffix: '+', label: 'رافعة متاحة' },
              { icon: 'ri-time-line', value: counts.years, suffix: '+', label: 'سنة خبرة' },
              { icon: 'ri-building-line', value: counts.projects, suffix: '+', label: 'مشروع منجز' },
              { icon: 'ri-team-line', value: counts.clients, suffix: '+', label: 'عميل راضٍ' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <i className={`${stat.icon} text-6xl text-accent`}></i>
                </div>
                <div className="text-7xl font-black text-white mb-2">
                  {stat.value}
                  <span className="text-accent">{stat.suffix}</span>
                </div>
                <p className="text-gray-300 text-lg">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black text-primary mb-4">
              خدماتنا
            </h2>
            <div className="w-20 h-1 bg-accent mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              نوفر مجموعة شاملة من الخدمات المتخصصة في تأجير الرافعات والمعدات الثقيلة
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-truck-fill',
                title: 'تأجير الرافعات الهيدروليكية',
                description: 'أسطول ضخم من الرافعات بقدرات من 7 إلى 300 طن',
              },
              {
                icon: 'ri-tools-fill',
                title: 'تأجير المعدات الإنشائية',
                description: 'معدات متنوعة لجميع أنواع المشاريع الإنشائية',
              },
              {
                icon: 'ri-settings-3-fill',
                title: 'الصيانة والإصلاح',
                description: 'خدمات صيانة دورية وإصلاح فوري للمعدات',
              },
              {
                icon: 'ri-roadster-fill',
                title: 'النقل والتوصيل',
                description: 'نقل المعدات إلى موقع مشروعك بأمان وسرعة',
              },
              {
                icon: 'ri-user-star-fill',
                title: 'توفير مشغلين محترفين',
                description: 'مشغلون مدربون ومعتمدون لتشغيل المعدات',
              },
              {
                icon: 'ri-customer-service-2-fill',
                title: 'الدعم الفني 24/7',
                description: 'فريق دعم فني متاح على مدار الساعة',
              },
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all group cursor-pointer"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-accent/10 group-hover:bg-accent rounded-xl mb-6 transition-all">
                  <i className={`${service.icon} text-4xl text-accent group-hover:text-primary transition-all`}></i>
                </div>
                <h3 className="text-xl font-bold text-primary mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cranes Overview Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-black text-primary mb-6">
              أسطول الرافعات
            </h2>
            <p className="text-2xl text-gray-600 max-w-3xl mx-auto">
              نوفر رافعات هيدروليكية بقدرات متنوعة من 7 إلى 300 طن لجميع أنواع المشاريع
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Heavy Cranes */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all"
            >
              <div className="relative h-80 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=massive%20yellow%20hydraulic%20crane%20160-300%20ton%20capacity%20at%20major%20construction%20site%20in%20Saudi%20Arabia%2C%20powerful%20heavy%20duty%20crane%20lifting%20steel%20beams%2C%20professional%20construction%20photography%2C%20dramatic%20low%20angle%20view&width=800&height=600&seq=heavy-crane-overview&orientation=landscape"
                  alt="رافعات ثقيلة"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent"></div>
                
                <div className="absolute top-6 right-6">
                  <span className="bg-red-600 text-white px-6 py-3 rounded-full font-black text-lg shadow-lg">
                    رافعات ثقيلة
                  </span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-4xl font-black text-white mb-3">160 - 300 طن</h3>
                  <p className="text-xl text-accent font-bold">للمشاريع الضخمة والبنية التحتية</p>
                </div>
              </div>

              <div className="p-8">
                <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                  رافعات هيدروليكية ضخمة بقدرات استثنائية، مثالية للأبراج الشاهقة والمنشآت الصناعية الكبرى والمشاريع الحكومية الضخمة.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-accent/10 rounded-xl">
                      <i className="ri-arrow-up-line text-2xl text-accent"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">ارتفاع الرفع</p>
                      <p className="font-black text-primary">حتى 120م</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-accent/10 rounded-xl">
                      <i className="ri-weight-line text-2xl text-accent"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">الحمولة</p>
                      <p className="font-black text-primary">160-300 طن</p>
                    </div>
                  </div>
                </div>

                <Link
                  to="/services"
                  className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-xl inline-flex items-center gap-3 w-full justify-center whitespace-nowrap cursor-pointer"
                >
                  <span>عرض التفاصيل</span>
                  <i className="ri-arrow-left-line text-xl"></i>
                </Link>
              </div>
            </motion.div>

            {/* Light Cranes */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all"
            >
              <div className="relative h-80 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=small%20mobile%20yellow%20crane%207-19%20ton%20capacity%20at%20residential%20construction%20site%20in%20Saudi%20Arabia%2C%20compact%20hydraulic%20crane%2C%20easy%20maneuvering%2C%20professional%20photography&width=800&height=600&seq=light-crane-overview&orientation=landscape"
                  alt="رافعات خفيفة"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent"></div>
                
                <div className="absolute top-6 right-6">
                  <span className="bg-green-600 text-white px-6 py-3 rounded-full font-black text-lg shadow-lg">
                    رافعات خفيفة
                  </span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-4xl font-black text-white mb-3">7 - 19 طن</h3>
                  <p className="text-xl text-accent font-bold">للمشاريع السكنية والتجارية</p>
                </div>
              </div>

              <div className="p-8">
                <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                  رافعات هيدروليكية خفيفة ومرنة، مثالية للمشاريع السكنية والتجارية الصغيرة والمتوسطة، سهلة المناورة في المساحات الضيقة.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-accent/10 rounded-xl">
                      <i className="ri-arrow-up-line text-2xl text-accent"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">ارتفاع الرفع</p>
                      <p className="font-black text-primary">حتى 35م</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-accent/10 rounded-xl">
                      <i className="ri-weight-line text-2xl text-accent"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">الحمولة</p>
                      <p className="font-black text-primary">7-19 طن</p>
                    </div>
                  </div>
                </div>

                <Link
                  to="/services"
                  className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-xl inline-flex items-center gap-3 w-full justify-center whitespace-nowrap cursor-pointer"
                >
                  <span>عرض التفاصيل</span>
                  <i className="ri-arrow-left-line text-xl"></i>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-5xl md:text-6xl font-black text-primary mb-6">مشاريعنا المنجزة</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                نفخر بإنجاز أكثر من 500 مشروع ناجح في جميع أنحاء المملكة
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              {
                title: 'برج الرياض التجاري',
                location: 'الرياض',
                image: 'massive construction project tall skyscraper building in Riyadh Saudi Arabia with multiple yellow cranes working, modern urban development, sunset golden hour lighting, wide angle aerial view',
                floors: '45 طابق',
                cranes: '8 رافعات ثقيلة',
              },
              {
                title: 'مجمع الأعمال الشرقي',
                location: 'الدمام',
                image: 'commercial business complex construction with yellow cranes, modern glass building in eastern Saudi Arabia, daytime clear sky, professional construction photography',
                floors: '25 طابق',
                cranes: '4 رافعات متوسطة',
              },
              {
                title: 'مشروع الطرق السريعة',
                location: 'جدة',
                image: 'highway infrastructure construction project with multiple cranes building bridge overpass in Saudi Arabia, road development, heavy construction equipment',
                floors: 'بنية تحتية',
                cranes: '12 رافعة متنوعة',
              },
            ].map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group cursor-pointer"
              >
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={`https://readdy.ai/api/search-image?query=$%7Bproject.image%7D&width=600&height=500&seq=home-project-${index + 1}-001&orientation=squarish`}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent"></div>
                  
                  <div className="absolute top-4 right-4">
                    <div className="bg-white/20 backdrop-blur-md px-4 py-2 rounded-full flex items-center gap-2">
                      <i className="ri-map-pin-fill text-accent"></i>
                      <span className="text-white font-bold text-sm">{project.location}</span>
                    </div>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-2xl font-black text-white mb-3">{project.title}</h3>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <i className="ri-building-line text-accent"></i>
                        <span className="text-white font-semibold">{project.floors}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-truck-line text-accent"></i>
                        <span className="text-white font-semibold">{project.cranes}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <a
              href="/projects"
              className="inline-flex items-center gap-3 bg-primary hover:bg-primary-dark text-white px-10 py-4 rounded-xl font-bold text-lg transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <span>عرض جميع المشاريع</span>
              <i className="ri-arrow-left-line text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-24 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="w-24 h-24 flex items-center justify-center bg-accent rounded-3xl mb-8">
                  <i className="ri-award-fill text-6xl text-primary"></i>
                </div>
                <h2 className="text-5xl font-black text-white mb-6">معتمدون دولياً</h2>
                <p className="text-xl text-gray-300 leading-relaxed mb-8">
                  جميع رافعاتنا ومعداتنا معتمدة من TÜV الألمانية، ونلتزم بأعلى معايير السلامة والجودة العالمية في جميع عملياتنا
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-xl">
                    <i className="ri-shield-check-fill text-3xl text-accent"></i>
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">شهادة TÜV</p>
                    <p className="text-gray-400 text-sm">فحص فني دوري</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="grid grid-cols-3 gap-6"
              >
                {[
                  { icon: 'ri-shield-star-fill', title: 'السلامة أولاً', desc: 'معايير عالمية' },
                  { icon: 'ri-user-star-fill', title: 'مشغلون محترفون', desc: 'مدربون ومعتمدون' },
                  { icon: 'ri-tools-fill', title: 'صيانة دورية', desc: 'فحص شامل' },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center hover:bg-white/20 transition-all cursor-pointer"
                  >
                    <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <i className={`${item.icon} text-4xl text-accent`}></i>
                    </div>
                    <h4 className="text-white font-bold text-sm mb-2">{item.title}</h4>
                    <p className="text-gray-400 text-xs">{item.desc}</p>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="contact" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=dramatic%20low%20angle%20view%20of%20massive%20yellow%20hydraulic%20crane%20boom%20extending%20into%20clear%20blue%20sky%2C%20powerful%20industrial%20construction%20equipment%2C%20professional%20crane%20photography%2C%20inspiring%20perspective%2C%20strong%20geometric%20composition&width=1920&height=1080&seq=cta-bg-001&orientation=landscape"
            alt="رافعة ضخمة"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-primary/85"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-24 h-24 flex items-center justify-center bg-accent rounded-full mx-auto mb-8">
              <i className="ri-phone-fill text-5xl text-primary"></i>
            </div>
            <h2 className="text-6xl md:text-7xl font-black text-white mb-6">جاهزون لخدمتك الآن</h2>
            <p className="text-2xl text-gray-300 mb-12 max-w-3xl mx-auto">
              تواصل معنا للحصول على استشارة مجانية وعرض سعر فوري لمشروعك
            </p>

            <div className="flex flex-wrap justify-center gap-6">
              <a
                href="tel:920000000"
                className="bg-accent hover:bg-accent-dark text-primary px-12 py-5 rounded-xl font-black text-xl transition-all shadow-2xl hover:shadow-accent/50 hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center gap-3"
              >
                <i className="ri-phone-fill text-2xl"></i>
                <span>اتصل بنا: 920000000</span>
              </a>
              <a
                href="https://wa.me/966500000000"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-transparent border-4 border-white hover:bg-white hover:text-primary text-white px-12 py-5 rounded-xl font-bold text-xl transition-all whitespace-nowrap cursor-pointer inline-flex items-center gap-3"
              >
                <i className="ri-whatsapp-fill text-2xl"></i>
                <span>أرسل طلبك عبر واتساب</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;